import { useMemo, useState } from 'react';
import { format, isWithinInterval, startOfDay, endOfDay, subMonths, addMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Loader2, Receipt } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useFinance } from '@/contexts/FinanceContext';
import { InstructionTrigger } from '@/features/instructions/InstructionDrawer';
import type { FinanceAccount } from '@/types/finance';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  card: FinanceAccount;
}

const fmtBrl = (n: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n);

function parseInputDate(value: string, fallback: Date): Date {
  if (!value) return fallback;
  const [y, m, d] = value.split('-').map((s) => Number.parseInt(s, 10));
  if (!y || !m || !d) return fallback;
  return new Date(y, m - 1, d);
}

function formatInputDate(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

/**
 * Calcula o ciclo padrão da fatura usando closing_day do cartão.
 * Se hoje já passou do closing_day, o ciclo é [closing_day do mês anterior, hoje].
 */
function defaultPeriod(closingDay: number | undefined): { start: Date; end: Date } {
  const today = new Date();
  if (!closingDay) {
    return {
      start: subMonths(today, 1),
      end: today,
    };
  }
  const thisMonthClosing = new Date(today.getFullYear(), today.getMonth(), closingDay);
  if (today >= thisMonthClosing) {
    // Período: do dia X do mês passado até dia X do mês atual (-1 dia, é o "fechamento")
    const start = new Date(today.getFullYear(), today.getMonth() - 1, closingDay);
    return { start, end: thisMonthClosing };
  }
  // Período: do dia X de 2 meses atrás até dia X do mês passado
  const start = new Date(today.getFullYear(), today.getMonth() - 2, closingDay);
  const end = new Date(today.getFullYear(), today.getMonth() - 1, closingDay);
  return { start, end };
}

function defaultPaymentDate(end: Date, dueDay: number | undefined): Date {
  if (!dueDay) return addMonths(end, 0);
  return new Date(end.getFullYear(), end.getMonth() + 1, dueDay);
}

export function CardInvoiceCloser({ open, onOpenChange, card }: Props) {
  const { transactions, accounts, closeCreditCardInvoice } = useFinance();

  const initial = defaultPeriod(card.closingDay);
  const [periodStart, setPeriodStart] = useState<Date>(initial.start);
  const [periodEnd, setPeriodEnd] = useState<Date>(initial.end);
  const [paymentAccountId, setPaymentAccountId] = useState<string>(() => {
    const checking = accounts.find((a) => a.type === 'checking' && a.id !== card.id);
    return (
      checking?.id ?? accounts.find((a) => a.id !== card.id && a.type !== 'credit_card')?.id ?? ''
    );
  });
  const [paymentDate, setPaymentDate] = useState<Date>(
    defaultPaymentDate(initial.end, card.dueDay),
  );
  const [submitting, setSubmitting] = useState(false);

  const cycleTransactions = useMemo(() => {
    const interval = { start: startOfDay(periodStart), end: endOfDay(periodEnd) };
    return transactions
      .filter((t) => t.accountId === card.id)
      .filter((t) => t.status === 'pending')
      .filter((t) => isWithinInterval(t.dueDate, interval))
      .sort((a, b) => a.dueDate.getTime() - b.dueDate.getTime());
  }, [transactions, card.id, periodStart, periodEnd]);

  const total = cycleTransactions.reduce((s, t) => s + Number(t.amount), 0);
  const paymentAccountOptions = accounts.filter(
    (a) => a.id !== card.id && a.type !== 'credit_card' && a.isActive,
  );

  const handleSubmit = async () => {
    if (cycleTransactions.length === 0 || !paymentAccountId) return;
    setSubmitting(true);
    try {
      await closeCreditCardInvoice({
        cardAccountId: card.id,
        periodStart: format(periodStart, 'yyyy-MM-dd'),
        periodEnd: format(periodEnd, 'yyyy-MM-dd'),
        paymentAccountId,
        paymentDate: format(paymentDate, 'yyyy-MM-dd'),
      });
      onOpenChange(false);
    } catch {
      // toast já foi emitido pelo context
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!submitting) onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-brand">
            <Receipt className="h-5 w-5 text-primary" />
            Fechar fatura — {card.name}
          </DialogTitle>
          <DialogDescription>
            Marque todas as transações pendentes do cartão no período como pagas e gere
            automaticamente o pagamento da fatura na conta de origem.{' '}
            <InstructionTrigger
              contextKey="close-card-invoice"
              variant="inline"
              label="Saiba mais"
            />
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Período — De</Label>
              <Input
                type="date"
                value={formatInputDate(periodStart)}
                onChange={(e) => setPeriodStart(parseInputDate(e.target.value, periodStart))}
                disabled={submitting}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Até</Label>
              <Input
                type="date"
                value={formatInputDate(periodEnd)}
                onChange={(e) => setPeriodEnd(parseInputDate(e.target.value, periodEnd))}
                disabled={submitting}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Conta de pagamento</Label>
              <Select
                value={paymentAccountId}
                onValueChange={setPaymentAccountId}
                disabled={submitting}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione…" />
                </SelectTrigger>
                <SelectContent>
                  {paymentAccountOptions.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Data do pagamento</Label>
              <Input
                type="date"
                value={formatInputDate(paymentDate)}
                onChange={(e) => setPaymentDate(parseInputDate(e.target.value, paymentDate))}
                disabled={submitting}
              />
            </div>
          </div>

          <div className="rounded-lg border border-border">
            <div className="flex items-center justify-between border-b border-border bg-secondary/50 px-3 py-2">
              <div className="text-sm">
                <span className="font-semibold">{cycleTransactions.length}</span> transação(ões) no
                período
              </div>
              <div className="text-sm">
                Total: <span className="font-semibold text-expense">{fmtBrl(total)}</span>
              </div>
            </div>
            {cycleTransactions.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                Nenhuma transação pendente neste período.
              </div>
            ) : (
              <div className="max-h-72 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[100px]">Data</TableHead>
                      <TableHead>Descrição</TableHead>
                      <TableHead className="text-right">Valor</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cycleTransactions.map((t) => (
                      <TableRow key={t.id}>
                        <TableCell className="text-xs">
                          {format(t.dueDate, 'dd/MM/yyyy', { locale: ptBR })}
                        </TableCell>
                        <TableCell className="max-w-[300px] truncate text-sm" title={t.description}>
                          {t.description}
                        </TableCell>
                        <TableCell className="text-right text-sm font-medium tabular-nums text-expense">
                          {fmtBrl(Number(t.amount))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || cycleTransactions.length === 0 || !paymentAccountId}
            className="bg-accent font-semibold text-accent-foreground hover:bg-accent/90"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Fechando…
              </>
            ) : (
              `Fechar fatura (${fmtBrl(total)})`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
